/* user.ts */
import DeviceConfig from '@devices/config';
import FlavourConfig from '../config/flavours';
import ImageUtils from '../utils/image';
import IParental from '../types/interfaces/parental';
import Plan from './plan';
import Storage from '@storage';
import { IChannelOrder } from '../types/interfaces/channel';
import { IDeviceRequest } from '../types/interfaces/login';
import { IPlanRequestData } from '../types/interfaces/plan';
import { store } from '../store/store';

class UserData {
  amazonAccount?: boolean;
  carrierId?: string;
  channelsOrder?: IChannelOrder[];
  email?: string;
  extraPlans?: IPlanRequestData[] | Plan[];
  isAnonymous?: boolean;
  mobile?: string;
  name?: string;
  parental?: IParental;
  planId?: string | null;
  postalCode?: string;
  token?: string | null;
  username?: string;
  userId?: string;
  isTest?: boolean;

  constructor(data: UserData) {
    this.amazonAccount = data.amazonAccount ?? false;
    this.carrierId = data.carrierId;
    this.channelsOrder = data.channelsOrder;
    this.email = data.email;
    this.extraPlans = data.extraPlans?.map((item) => {
      return new Plan(item as IPlanRequestData);
    });
    this.isAnonymous = data.isAnonymous;
    this.mobile = data.mobile;
    this.name = data.name;
    this.planId = data.planId;
    this.postalCode = data.postalCode;
    this.token = data.token;
    this.username = data.username;
    this.userId = data.userId;
    this.isTest = data.isTest || false;
  }
}

class UserMetadata {
  city: string;

  constructor(metadata: UserMetadata) {
    this.city = metadata.city;
  }
}

class UserOperator {
  cdnBalancer?: {
    active: boolean;
    platforms: string[];
  };
  code?: string;
  freemiumFilter?: boolean;
  isP2P?: boolean;
  landing?: string;
  moreContentEnabled?: boolean;
  mySpaceEnabled?: boolean;
  name?: string;
  npaw?: {
    active: boolean;
  };
  omnitrack?: {
    active: boolean;
    analytics_frequency?: number;
    ping_frequency?: number;
    inactivity_sesion_time?: number;
    inactivity_session_time?: number;
    platforms: string[];
  };
  p2p?: boolean;
  serviceLogo?: string;

  constructor(operator: UserOperator) {
    this.code = operator.code;
    this.isP2P = (operator.isP2P || operator.p2p) ?? false;
    this.landing = operator.landing;
    this.moreContentEnabled = operator.moreContentEnabled ?? false;
    this.mySpaceEnabled = operator.mySpaceEnabled ?? false;
    this.name = operator.name;
    this.npaw = operator.npaw;
    this.serviceLogo = operator.serviceLogo;
  }
}

class User {
  data: UserData;
  metadata?: UserMetadata;
  operator?: UserOperator;

  constructor(
    data: UserData,
    metadata?: UserMetadata,
    operator?: UserOperator
  ) {
    this.data = {
      userId: data.userId || '',
      email: data.email || '',
      ...data,
    };
    if (metadata) {
      this.metadata = new UserMetadata(metadata);
    }
    if (operator) {
      this.operator = new UserOperator(operator);
    }
    if (this.data?.token) {
      Storage.set('userToken', this.data.token);
    }
  }

  getData() {
    const {
      carrierId,
      email,
      isAnonymous,
      mobile,
      name,
      parental,
      extraPlans,
      postalCode,
      token,
      username,
      userId,
      isTest,
    } = this.data;

    return {
      carrierId,
      email,
      isAnonymous,
      metadata: this.metadata,
      mobile,
      name,
      operator: this.operator,
      parental,
      extraPlans,
      postalCode,
      token,
      username,
      userId,
      isTest,
    };
  }

  static getPlan(planId: string | null | undefined) {
    const { app, plans } = store.getState();
    if (planId) {
      return plans?.data?.find((item) => {
        return item.id === planId;
      });
    } else if (app.status.anonymous) {
      return plans?.data?.find((item) => {
        return item.anonymous;
      });
    }
  }

  static fromRequest(response: IDeviceRequest) {
    const {
      amazonAccount,
      city,
      channels,
      email,
      extraPlans,
      plan,
      carrier,
      _id,
      id,
      mobile,
      name,
      parental,
      postcode,
      token,
      username,
    } = response?.data || {};

    const data = {
      amazonAccount,
      carrierId: carrier.id,
      channelsOrder: channels,
      email,
      extraPlans,
      isAnonymous: false,
      mobile,
      name,
      parental,
      planId: plan._id || plan.id,
      isTest: plan?.code === 'tivitest',
      postalCode: postcode,
      token,
      username,
      userId: _id || id,
    };
    const metadata = {
      city,
    };
    const operator = {
      code: carrier?.code,
      p2p:
        carrier?.p2p?.isP2P &&
        carrier?.p2p?.platforms.includes(DeviceConfig.info.make),
      landing: carrier?.upgradeLandingUrl,
      serviceLogo: ImageUtils.getImage(
        carrier?.logoBlanco || FlavourConfig.images.bwLogo
      ),
      moreContentEnabled: carrier?.moreContentEnabled,
      mySpaceEnabled: carrier?.mySpaceEnabled,
      name: carrier?.name,
      npaw: carrier?.npaw,
    };
    return new User(data as UserData, metadata, operator as UserOperator);
  }

  static anonymous() {
    return new User({ isAnonymous: true }, undefined);
  }

  static default() {
    return new User({
      isAnonymous: false,
      token: Storage.get('userToken') || undefined,
    });
  }
}

export { UserOperator };
export default User;
